import { useState, useEffect, useRef } from "react";
import CountdownTimer from "./CountdownTimer";
import { buildMediaUrl } from "../../../utils/mediaType";

interface QuestionImgProps {
  media: string;
  retryKey?: number;
  onError?: () => void;
  onLoad?: () => void;
  /** When provided, show a countdown timer overlay inside the full-screen image preview. */
  showPreviewTimer?: boolean;
  /** Read current elapsed/remaining seconds when the preview opens (avoids parent re-renders each tick). */
  getPreviewTimerSeconds?: () => number;
  /** Fallback initial time (seconds) for standalone preview countdowns (e.g. special cases). */
  previewTimerInitialTime?: number;
  /** When true, preview timer runs in elapsed mode (count-up) instead of countdown. */
  previewTimerElapsedMode?: boolean;
}

const QuestionImg = ({
  media,
  retryKey = 0,
  onError,
  onLoad,
  showPreviewTimer,
  getPreviewTimerSeconds,
  previewTimerInitialTime,
  previewTimerElapsedMode,
}: QuestionImgProps) => {
  const mediaUrl = buildMediaUrl(media, retryKey);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewTimerSnapshot, setPreviewTimerSnapshot] = useState(0);
  const getPreviewTimerSecondsRef = useRef(getPreviewTimerSeconds);
  getPreviewTimerSecondsRef.current = getPreviewTimerSeconds;

  // Sync preview timer with the main game timer (refs only — re-renders this modal, not the full page)
  useEffect(() => {
    if (!isPreviewOpen || !showPreviewTimer) return;

    const syncFromMainTimer = () => {
      setPreviewTimerSnapshot(getPreviewTimerSecondsRef.current?.() ?? 0);
    };

    syncFromMainTimer();
    const interval = setInterval(syncFromMainTimer, 1000);
    return () => clearInterval(interval);
  }, [isPreviewOpen, showPreviewTimer]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isPreviewOpen) {
        setIsPreviewOpen(false);
      }
    };

    if (isPreviewOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isPreviewOpen]);

  const handleImageClick = () => {
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClosePreview();
    }
  };

  return (
    <>
      <div className="2xl:max-h-[56vh] xl:max-h-[53vh] lg:max-h-[50vh] h-full max-h-[200px] landscape:max-lg:max-h-[50vh]  flex-1 2xl:max-w-full xl:max-w-[500px] max-w-[400px] landscape:max-lg:max-w-[50vw] 2xl:mt-[0.5vw] lg:mt-3 mt-1 w-full overflow-hidden">
        <img
          src={mediaUrl}
          alt="Question Image"
          onClick={handleImageClick}
          onError={onError}
          onLoad={onLoad}
          className="w-full h-full object-contain cursor-pointer transition-opacity"
        />
      </div>

      {/* Fullscreen Preview Modal */}
      {isPreviewOpen && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50 h-full w-full"
          onClick={handleBackdropClick}
        >
          {/* Close Button */}
          <button
            onClick={handleClosePreview}
            className="absolute 2xl:top-[2vw] 2xl:right-[2vw] lg:top-8 lg:right-8 top-4 right-4 lg:w-[1.2vw] lg:h-[1.2vw] md:w-7 w-7 md:h-7 h-7 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300 outline-none z-10"
            aria-label="Close preview"
          >
            <svg
              className="w-full h-full"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                fill="white"
              />
            </svg>
          </button>

          {/* Preview Image with optional timer overlay */}
          <div className="relative w-full h-full flex items-center justify-center">
            {showPreviewTimer && (
              <div
                className="absolute bottom-4 right-4 z-10"
                onClick={(e) => e.stopPropagation()}
              >
                <CountdownTimer
                  initialTime={
                    previewTimerElapsedMode
                      ? previewTimerSnapshot
                      : previewTimerInitialTime ?? 0
                  }
                  initialRemainingOverride={
                    previewTimerElapsedMode ? undefined : previewTimerSnapshot
                  }
                  elapsedMode={previewTimerElapsedMode}
                  elapsedRedThreshold={55}
                  // Display-only mirror of the main timer; time is synced via previewTimerSnapshot.
                  hideIcons
                  disablePause
                  isStopped
                  autoStart={false}
                />
              </div>
            )}
            <img
              src={mediaUrl}
              alt="Preview"
              onError={onError}
          onLoad={onLoad}
              className="p-4 w-full h-full object-contain animate-[slideUp_0.3s_ease-out]"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};
export default QuestionImg;
